import { ReactElement, useState, useCallback, useEffect, useMemo } from 'react';
import {
  DialogTitle,
  DialogContentText,
  Button,
  Link,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useFormik, FormikProvider, FormikHelpers } from 'formik';
import { useSnackbar } from 'notistack';

import { FileUploadV2, ImporterProgress } from '~components/index';
import {
  useLazyRequest,
  useSampleTemplates,
  useTypePartnerCompany,
} from '~hooks/index';
import { importPartnerCompaniesByCSV } from '~services/partnerCompany';
import { PartnerCompanyImporter } from '~services/partnerCompany/types';
import { getImporterPartnerCompaniesFromCSVAlertDescription } from '~utils/partnerCompany';
import { toAbsoluteUrl } from '~utils/assetsHelpers';

import { DialogImportPartnerCompaniesFromCSVProps } from './types';
import {
  DialogImportPartnerCompaniesFromCSVContainer,
  DialogImportPartnerCompaniesFromCSVContent,
  DialogImportPartnerCompaniesFromCSVActions,
} from './styles';
import { FIELDS_NAME, initialValues, validationSchema } from './utils';

const defaultImportedPartnerCompaniesData: PartnerCompanyImporter = {
  errorCount: 0,
  errors: [],
  message: '',
  successCount: 0,
};

const DialogImportPartnerCompaniesFromCSV = ({
  open,
  onClose,
  onSubmitSuccess,
}: DialogImportPartnerCompaniesFromCSVProps): ReactElement => {
  const { enqueueSnackbar } = useSnackbar();
  const { isCarrier } = useTypePartnerCompany();

  const { partnerCompanies: dowloadTemplateUrl } = useSampleTemplates();

  const [runImporter, setRunImporter] = useState(false);
  const [isCompletedRequest, setIsCompletedRequest] = useState(false);
  const [progress, setProgress] = useState(0);
  const [importedPartnerCompaniesData, setImportedPartnerCompaniessData] =
    useState<PartnerCompanyImporter>(defaultImportedPartnerCompaniesData);

  const [, loadingImportPartnerCompanies, , executeImportPartnerCompanies] =
    useLazyRequest({
      request: importPartnerCompaniesByCSV,
      withPostSuccess: (response) => {
        const responseImporter = response.data?.data as PartnerCompanyImporter;

        setIsCompletedRequest(true);

        setTimeout(() => {
          setProgress(100);
          setImportedPartnerCompaniessData(responseImporter);
          onSubmitSuccess();

          if (responseImporter.errorCount === 0) {
            onClose();
          }
        }, 1500);
      },
      withPostFailure: () => {
        setIsCompletedRequest(true);
        enqueueSnackbar('Ha ocurrido un error, intente nuevamente', {
          variant: 'error',
        });
      },
    });

  const isCompletedProgress = useMemo(() => progress === 100, [progress]);

  const isImporting = useMemo(
    () => loadingImportPartnerCompanies || progress === 100 || runImporter,
    [loadingImportPartnerCompanies, progress, runImporter],
  );

  const isCompletedProcessImporter = useMemo(
    () => isCompletedProgress && isCompletedRequest,
    [isCompletedProgress, isCompletedRequest],
  );

  const importerProgressTitle = useMemo(
    () =>
      isCompletedProcessImporter
        ? 'Archivo importado'
        : 'Importando desde archivo',
    [isCompletedProcessImporter],
  );

  const importerProgressDescription = useMemo(
    () =>
      isCompletedProcessImporter
        ? getImporterPartnerCompaniesFromCSVAlertDescription(
            importedPartnerCompaniesData,
          )
        : 'Importando',
    [isCompletedProcessImporter, importedPartnerCompaniesData],
  );

  const importerProgressErrors = useMemo(() => {
    if (!isCompletedProcessImporter) return undefined;

    return importedPartnerCompaniesData.errors.map(
      (error) => `${error.error} (Fila: ${error.row})`,
    );
  }, [isCompletedProcessImporter, importedPartnerCompaniesData.errors]);

  const onSubmit = useCallback(
    async (
      values: typeof initialValues,
      { setSubmitting }: FormikHelpers<typeof initialValues>,
    ) => {
      setRunImporter(true);
      setIsCompletedRequest(false);
      setProgress(0);
      await executeImportPartnerCompanies(values[FIELDS_NAME.FILES]);

      setSubmitting(false);
    },
    [executeImportPartnerCompanies],
  );

  const formikBag = useFormik({
    initialValues,
    validationSchema,
    onSubmit,
  });

  const { resetForm, submitForm, isSubmitting, dirty, isValid, values } =
    formikBag;

  useEffect(() => {
    if (!open) {
      setTimeout(() => {
        resetForm();
        setRunImporter(false);
        setIsCompletedRequest(false);
        setProgress(0);
        setImportedPartnerCompaniessData(defaultImportedPartnerCompaniesData);
      }, 500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const disabledSubmit = useMemo(
    () => !dirty || !isValid || !values[FIELDS_NAME.FILES],
    [dirty, isValid, values],
  );

  return (
    <FormikProvider value={formikBag}>
      <DialogImportPartnerCompaniesFromCSVContainer
        open={open}
        onClose={onClose}
        maxWidth="sm"
      >
        <DialogTitle>Importar Dadores de carga asociados</DialogTitle>

        <DialogImportPartnerCompaniesFromCSVContent dividers>
          {!isImporting && (
            <DialogContentText color="text.primary">
              Importar dadores de carga desde un archivo de forma masiva
            </DialogContentText>
          )}

          <FileUploadV2
            name={FIELDS_NAME.FILES}
            labelImg={{
              src: toAbsoluteUrl('/images/import_file.png'),
              alt: 'Import File',
            }}
            accept=".csv, .xls, .xlsx"
            disabled={isImporting}
          />

          {!isImporting && (
            <DialogContentText variant="caption">
              Si deseas importar de forma masiva utilizando un archivo, puedes{' '}
              <Link
                href={dowloadTemplateUrl}
                download
                color="primary"
                underline="hover"
                fontWeight="bold"
              >
                descargar y modificar la plantilla de ejemplo.
              </Link>{' '}
              Recuerda respetar el orden de las columnas.{' '}
              <Typography
                variant="inherit"
                component="span"
                // @NEW-GIVER-DASHBOARD: Hide until the help URL is available for Givers
                visibility={isCarrier ? 'visible' : 'hidden'}
              >
                Si necesitasinformación consulta la{' '}
                <Link
                  href="https://routix.tawk.help" // @TODO: Verify and change
                  download
                  color="primary"
                  underline="hover"
                  fontWeight="bold"
                >
                  ayuda.
                </Link>
              </Typography>
            </DialogContentText>
          )}

          {isImporting && (
            <ImporterProgress
              title={importerProgressTitle}
              description={importerProgressDescription}
              errors={importerProgressErrors}
              progress={progress}
              warning={importedPartnerCompaniesData.errorCount >= 1}
            />
          )}
        </DialogImportPartnerCompaniesFromCSVContent>

        <DialogImportPartnerCompaniesFromCSVActions>
          <Button color="secondary" onClick={onClose}>
            Cerrar
          </Button>

          {!isImporting && (
            <LoadingButton
              variant="contained"
              color="primary"
              onClick={submitForm}
              loading={loadingImportPartnerCompanies || isSubmitting}
              disabled={disabledSubmit}
            >
              Importar
            </LoadingButton>
          )}
        </DialogImportPartnerCompaniesFromCSVActions>
      </DialogImportPartnerCompaniesFromCSVContainer>
    </FormikProvider>
  );
};

export default DialogImportPartnerCompaniesFromCSV;
